import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { MiscellaneousField, VideoSegment } from '../../reportsTypes';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect } from 'react';
import { PiPlusThin } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { ShapeSelect } from '../../../Shapes/ShapeSelects';
import { v4 as uuidv4 } from 'uuid';
import { DateRange } from 'react-day-picker';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';

interface VideoSegmentsItemProps {
  fieldName: string;
  form: UseFormReturn<FormValues>;
  fieldInfo: MiscellaneousField;
  dateRange: DateRange | undefined;
}
interface FormValues {
  [key: string]: VideoSegment[];
}

interface VideoSegmentDateItemProps {
  dateRange: DateRange | undefined;
  index: number;
  fieldName: string;
  form: UseFormReturn<FormValues>;
  fields: VideoSegment[];
}

const VideoSegmentDateItem = ({
  dateRange,
  index,
  fieldName,
  form,
  fields,
}: VideoSegmentDateItemProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Get current dateRange from form
  const currentDateRange = form.watch(`${fieldName}.${index}.dateRange`);

  // Set default date range on component mount
  useEffect(() => {
    if (!currentDateRange && dateRange?.from && dateRange?.to) {
      const defaultDateRange: DateRange = {
        from: new Date(dateRange.from.getTime() + 1000), // 1 second after start
        to: new Date(dateRange.to.getTime() - 1000), // 1 second before end
      };

      form.setValue(
        `${fieldName}[${index}].dateRange`,
        defaultDateRange as never,
      );

      // Update fields array
      if (fields[index]) {
        fields[index].dateRange = defaultDateRange;
      }
    }
  }, [dateRange, currentDateRange, form, fieldName, index, fields]);

  const validateDateRange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      const errors: { from?: string; to?: string } = {};

      // Check if dates are within allowed range
      if (dateRange?.from && from && from < dateRange.from) {
        errors.from = 'Start date is before the allowed range';
      }
      if (dateRange?.to && from && from > dateRange.to) {
        errors.from = 'Start date is after the allowed range';
      }
      if (dateRange?.from && to && to < dateRange.from) {
        errors.to = 'End date is before the allowed range';
      }
      if (dateRange?.to && to && to > dateRange.to) {
        errors.to = 'End date is after the allowed range';
      }

      // Check if end date is after start date
      if (from && to && to <= from) {
        errors.to = 'End date must be after start date';
      }

      return errors;
    },
    [dateRange],
  );

  const handleFromDateChange = useCallback(
    (selectedDate: Date | undefined) => {
      const newDateRange: DateRange = {
        from: selectedDate,
        to: currentDateRange?.to,
      };

      // Validate both dates
      const errors = validateDateRange(newDateRange.from, newDateRange.to);

      // Update form value
      form.setValue(`${fieldName}[${index}].dateRange`, newDateRange as never);

      // Handle validation errors
      if (errors.from || errors.to) {
        const errorMessage = errors.from || errors.to || 'Invalid date range';
        form.setError(`${fieldName}.${index}.dateRange`, {
          type: 'manual',
          message: errorMessage,
        });
      } else {
        form.clearErrors(`${fieldName}.${index}.dateRange`);
      }

      // Update fields array
      if (fields[index]) {
        fields[index].dateRange = newDateRange;
      }
    },
    [form, fieldName, index, currentDateRange?.to, validateDateRange, fields],
  );

  const handleToDateChange = useCallback(
    (selectedDate: Date | undefined) => {
      const newDateRange: DateRange = {
        from: currentDateRange?.from,
        to: selectedDate,
      };

      // Validate both dates
      const errors = validateDateRange(newDateRange.from, newDateRange.to);

      // Update form value
      form.setValue(`${fieldName}[${index}].dateRange`, newDateRange as never);

      // Handle validation errors
      if (errors.from || errors.to) {
        const errorMessage = errors.from || errors.to || 'Invalid date range';
        form.setError(`${fieldName}.${index}.dateRange`, {
          type: 'manual',
          message: errorMessage,
        });
      } else {
        form.clearErrors(`${fieldName}.${index}.dateRange`);
      }

      // Update fields array
      if (fields[index]) {
        fields[index].dateRange = newDateRange;
      }
    },
    [form, fieldName, index, currentDateRange?.from, validateDateRange, fields],
  );

  // Get form error for the dateRange field
  const dateRangeError =
    form.formState.errors?.[fieldName]?.[index]?.dateRange?.message;

  return (
    <div className="space-y-4 col-span-2">
      <div className="flex justify-between">
        <Label className="text-xs">Period</Label>
        {dateRange?.from && dateRange?.to && (
          <InfoIconTooltip>
            choose between {formatDateTimeWithTimeZone(dateRange.from)} and{' '}
            {formatDateTimeWithTimeZone(dateRange.to)}
          </InfoIconTooltip>
        )}
      </div>
      <div className="flex space-x-5">
        <div className="space-y-1">
          <Label className="text-xs">Start Date *</Label>
          <DateTimePickerField
            value={currentDateRange?.from}
            onChange={handleFromDateChange}
            dateLimit="past"
            startDatetimeThreshold={dateRange?.from}
            endDatetimeThreshold={dateRange?.to}
            placeholder="Select start date and time"
            className={dateRangeError ? 'border-red-500' : ''}
          />
        </div>

        {/* End Date */}
        <div className="space-y-1">
          <Label className="text-xs">End Date *</Label>
          <DateTimePickerField
            value={currentDateRange?.to}
            onChange={handleToDateChange}
            dateLimit="past"
            startDatetimeThreshold={currentDateRange?.from || dateRange?.from}
            endDatetimeThreshold={dateRange?.to}
            placeholder="Select end date and time"
            className={dateRangeError ? 'border-red-500' : ''}
          />
        </div>

        {/* Show error message once for the entire date range */}
        {dateRangeError && (
          <p className="text-xs text-red-500">{dateRangeError}</p>
        )}
      </div>
    </div>
  );
};

export const MiscellaneousVideoSegmentsItem = ({
  fieldName,
  form,
  fieldInfo,
  dateRange, // This dateRange is the one picked by the user earlier
}: VideoSegmentsItemProps) => {
  const { messages } = useLocalization();
  const { fields, append, remove } = useFieldArray<FormValues>({
    control: form.control,
    name: fieldName as never,
  }) as unknown as {
    fields: VideoSegment[];
    append: (...args: unknown[]) => void;
    remove: (...args: unknown[]) => void;
  };

  return (
    <FormField
      key={fieldName}
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <FormLabel>
              {fieldInfo.name}: {fieldInfo.required && '*'}
            </FormLabel>
            {fieldInfo.help && (
              <InfoIconTooltip>{fieldInfo.help}</InfoIconTooltip>
            )}
          </div>
          <FormControl onChange={() => form.clearErrors()}>
            <div>
              {fields.map((videoSegment: VideoSegment, index) => (
                <div
                  key={videoSegment.id}
                  className="grid grid-cols-2 gap-4 p-2 border rounded-lg"
                >
                  <div className="flex col-span-2 mt-2 space-x-4 items-start justify-between">
                    <Label className="">Segment {index + 1}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="-mt-3"
                          onClick={() => remove(index)}
                        >
                          <XIcon className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Remove segment</TooltipContent>
                    </Tooltip>
                  </div>
                  <VideoSegmentDateItem
                    dateRange={dateRange}
                    form={form}
                    index={index}
                    fieldName={fieldName}
                    fields={fields}
                  />
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Shape *</Label>
                    </div>
                    <ShapeSelect
                      value={videoSegment.shapeId}
                      onChange={(shapeId) => {
                        form.setValue(
                          `${fieldName}[${index}].shapeId`,
                          shapeId as never,
                        );
                        fields[index].shapeId = shapeId;
                      }}
                    ></ShapeSelect>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Video speed</Label>
                    </div>
                    <Select
                      value={videoSegment.videoSpeed.toString()}
                      onValueChange={(value) => {
                        const intValue = parseInt(value, 10);
                        form.setValue(
                          `${fieldName}[${index}].videoSpeed`,
                          intValue as never,
                        );
                        fields[index].videoSpeed = intValue;
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a video speed" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="1" value="1">
                          1x
                        </SelectItem>
                        <SelectItem key="2" value="2">
                          2x
                        </SelectItem>
                        <SelectItem key="3" value="4">
                          4x
                        </SelectItem>
                        <SelectItem key="4" value="8">
                          8x
                        </SelectItem>
                        <SelectItem key="5" value="16">
                          16x
                        </SelectItem>
                        <SelectItem key="6" value="32">
                          32x
                        </SelectItem>
                        <SelectItem key="7" value="64">
                          64x
                        </SelectItem>
                        <SelectItem key="8" value="128">
                          128x
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-8 p-2 pb-4">
                    <div className="flex space-x-4 items-center">
                      <Label className="text-xs">Show labels</Label>
                      <Switch
                        checked={videoSegment.showLabels}
                        onCheckedChange={(checked) => {
                          form.setValue(
                            `${fieldName}[${index}].showLabels`,
                            checked as never,
                          );
                          fields[index].showLabels = checked;
                        }}
                      />
                    </div>
                    <div className="flex space-x-4 items-center">
                      <Label className="text-xs">Color by ships</Label>
                      <Switch
                        checked={videoSegment.showAllShips}
                        onCheckedChange={(checked) => {
                          form.setValue(
                            `${fieldName}[${index}].showAllShips`,
                            checked as never,
                          );
                          fields[index].showAllShips = checked;
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9"
                    onClick={() => {
                      const defaultDateRange =
                        dateRange?.from && dateRange?.to
                          ? {
                              from: new Date(dateRange.from.getTime() + 1000), // 1 second after start
                              to: new Date(dateRange.to.getTime() - 1000), // 1 second before end
                            }
                          : undefined;

                      append({
                        id: uuidv4(),
                        videoSpeed: 1,
                        showAllShips: true,
                        showLabels: true,
                        dateRange: defaultDateRange,
                      });
                    }}
                  >
                    <PiPlusThin className="size-4"></PiPlusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add new video segment</TooltipContent>
              </Tooltip>
            </div>
          </FormControl>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
